import Phaser from 'phaser';

/**
 * 원형 월드맵 관리
 * 원형 경계와 영역 렌더링
 */
export class CircularWorld {
  private graphics: Phaser.GameObjects.Graphics;
  private territoriesGraphics: Phaser.GameObjects.Graphics;
  private centerX: number;
  private centerY: number;
  private radius: number;
  private territories: Map<string, Territory> = new Map();

  constructor(scene: Phaser.Scene, centerX: number, centerY: number, radius: number) {
    this.centerX = centerX;
    this.centerY = centerY;
    this.radius = radius;

    // 맵 배경/경계 렌더링용 그래픽 (가장 아래)
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(0);

    // 영역 렌더링용 그래픽 (배경 위)
    this.territoriesGraphics = scene.add.graphics();
    this.territoriesGraphics.setDepth(1);

    this.drawBoundary();
  }

  private drawBoundary(): void {
    this.graphics.clear();

    // 맵 배경 (흰색)
    this.graphics.fillStyle(0xFFFFFF, 1);
    this.graphics.fillCircle(this.centerX, this.centerY, this.radius);

    // 외곽 경계선
    this.graphics.lineStyle(4, 0x2C3E50, 1);
    this.graphics.strokeCircle(this.centerX, this.centerY, this.radius);
  }

  isInsideBoundary(x: number, y: number): boolean {
    const dist = Phaser.Math.Distance.Between(x, y, this.centerX, this.centerY);
    return dist <= this.radius - 20; // 약간의 여유
  }

  clampToBoundary(x: number, y: number): { x: number; y: number } {
    const dist = Phaser.Math.Distance.Between(x, y, this.centerX, this.centerY);
    const maxDist = this.radius - 25;

    if (dist <= maxDist) {
      return { x, y };
    }

    const angle = Math.atan2(y - this.centerY, x - this.centerX);
    return {
      x: this.centerX + Math.cos(angle) * maxDist,
      y: this.centerY + Math.sin(angle) * maxDist,
    };
  }

  /**
   * 플레이어 영역 추가/업데이트
   */
  addTerritory(playerId: string, points: { x: number; y: number }[], color: number): void {
    let territory = this.territories.get(playerId);

    if (!territory) {
      territory = { playerId, regions: [], color };
      this.territories.set(playerId, territory);
    }

    territory.color = color;
    territory.regions.push(points);

    this.renderTerritories();
  }

  /**
   * 영역을 완전히 새로 설정
   */
  setTerritory(playerId: string, regions: { x: number; y: number }[][], color: number): void {
    this.territories.set(playerId, { playerId, regions, color });
    this.renderTerritories();
  }

  /**
   * 트레일 경로로 영역 채우기 (폐곡선)
   */
  fillClosedPath(playerId: string, pathPoints: { x: number; y: number }[], color: number): void {
    if (pathPoints.length < 3) return;

    let territory = this.territories.get(playerId);
    if (!territory) {
      territory = { playerId, regions: [], color };
      this.territories.set(playerId, territory);
    }

    territory.color = color;
    territory.regions.push(pathPoints);

    this.renderTerritories();
  }

  private renderTerritories(): void {
    this.territoriesGraphics.clear();

    this.territories.forEach((territory) => {
      this.territoriesGraphics.fillStyle(territory.color, 0.6);

      for (const region of territory.regions) {
        if (region.length < 3) continue;

        this.territoriesGraphics.beginPath();
        this.territoriesGraphics.moveTo(region[0].x, region[0].y);

        // 부드러운 곡선을 위한 catmull-rom spline 또는 bezier 적용
        for (let i = 1; i < region.length; i++) {
          this.territoriesGraphics.lineTo(region[i].x, region[i].y);
        }

        this.territoriesGraphics.closePath();
        this.territoriesGraphics.fillPath();

        // 외곽선
        this.territoriesGraphics.lineStyle(2, this.darkenColor(territory.color, 0.3), 1);
        this.territoriesGraphics.strokePath();
      }
    });
  }

  private darkenColor(color: number, amount: number): number {
    const r = Math.max(0, ((color >> 16) & 0xFF) * (1 - amount));
    const g = Math.max(0, ((color >> 8) & 0xFF) * (1 - amount));
    const b = Math.max(0, (color & 0xFF) * (1 - amount));
    return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
  }

  getCenter(): { x: number; y: number } {
    return { x: this.centerX, y: this.centerY };
  }

  getRadius(): number {
    return this.radius;
  }

  clearTerritory(playerId: string): void {
    this.territories.delete(playerId);
    this.renderTerritories();
  }

  destroy(): void {
    this.graphics.destroy();
    this.territoriesGraphics.destroy();
  }
}

interface Territory {
  playerId: string;
  regions: { x: number; y: number }[][];
  color: number;
}
